/**
 * Vue eleve d'un exercice HTML/CSS.
 *
 * Ce composant orchestre : il compose l'enonce, l'editeur, l'apercu et le
 * retour de correction, et delegue toute la logique aux modules de `core/` et
 * aux hooks. Il portait auparavant a lui seul le chargement, la validation, la
 * sauvegarde, le rendu de l'apercu, sa propre feuille de style et ses propres
 * fenetres modales.
 */

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Spin, message } from 'antd';
import {
  ExportOutlined,
  PlayCircleOutlined,
  RollbackOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { TLesson } from '../../models/courses/lesson';
import { useUserApi } from '../../api/user/userApp';

import { HtmlCssWorkspace, WinEvaluation } from './core/types';
import { extractConfigContent } from './core/config';
import { evaluateWinConditions } from './core/winConditions';
import { buildPreviewDocument } from './core/preview';

import { useHtmlCssWorkspace } from './hooks/useHtmlCssWorkspace';
import { useHtmlCssPersistence } from './hooks/useHtmlCssPersistence';

import ResponsiveHtmlCssLayout from './ResponsiveHtmlCssLayout';
// Import explicite : ce composant applique ses propres classes (`htmlcss-root`,
// `htmlcss-loading`) y compris quand le layout n'est pas encore rendu.
import './ResponsiveHtmlCssLayout.css';
import HtmlCssPreview from './components/HtmlCssPreview';
import HtmlCssStatement from './components/HtmlCssStatement';
import WinConditionFeedback from './components/WinConditionFeedback';
import HelpDrawer from './components/HelpDrawer';
import HtmlCssEditorPane, {
  EditorTab,
  HtmlCssEditorPaneRef,
} from './components/HtmlCssEditorPane';

interface HtmlCssStudentProps {
  lesson: TLesson;
  checkResult: (isValid: boolean) => void;
  /** Enonce fourni par la vue parente ; a defaut celui de la lecon est utilise. */
  statement?: string;
  isAdmin?: boolean;
  /**
   * Mode projet : le travail n'appartient pas a une lecon.
   *
   * La sauvegarde et la progression sont alors prises en charge par la vue
   * parente, qui ecrit dans la configuration du projet. Sans ce drapeau, le
   * composant appelait l'API des reponses de lecon avec un identifiant de
   * projet, ce que le serveur rejette (500).
   */
  isProject?: boolean;
  onAdminAction?: (action: 'win' | 'help' | 'save') => void;
  enableEditCode?: boolean;
  currentHtmlCode?: string;
  currentCssCode?: string;
  onHtmlCodeChange?: (code: string) => void;
  onCssCodeChange?: (code: string) => void;
  onRestoreInitial?: () => void;
}

export interface HtmlCssStudentRef {
  getCurrentCode: () => HtmlCssWorkspace;
}

/** Panneau d'aide actuellement ouvert. */
type OpenHelp = 'html' | 'css' | 'selector' | null;

const HtmlCssStudent = React.forwardRef<HtmlCssStudentRef, HtmlCssStudentProps>(
  (
    {
      lesson,
      checkResult,
      statement,
      isAdmin = false,
      isProject = false,
      onAdminAction,
      enableEditCode = true,
      currentHtmlCode,
      currentCssCode,
      onHtmlCodeChange,
      onCssCodeChange,
      onRestoreInitial,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { saveProgressionUser } = useUserApi();

    const config = useMemo(() => extractConfigContent(lesson?.config), [lesson?.config]);

    // En mode administration, le code affiche est pilote par la vue parente.
    const controlledWorkspace = useMemo<HtmlCssWorkspace | undefined>(
      () =>
        isAdmin && currentHtmlCode !== undefined && currentCssCode !== undefined
          ? { html: currentHtmlCode, css: currentCssCode }
          : undefined,
      [isAdmin, currentHtmlCode, currentCssCode]
    );

    const { workspace, loading, setHtml, setCss, restoreInitial, answerId, restoredFromLocal } =
      useHtmlCssWorkspace({
        lessonId: lesson?.id ?? 0,
        config,
        // Le mode projet se comporte ici comme le mode administration : le code
        // vient de la configuration, pas d'une reponse de lecon a charger.
        isAdmin: isAdmin || isProject,
        controlledWorkspace,
      });

    const { save, saving, setAnswerId } = useHtmlCssPersistence({
      lessonId: lesson?.id ?? 0,
      // En mode projet, l'identifiant est celui d'un projet : l'API des
      // reponses de lecon le rejetterait. La vue parente enregistre elle-meme.
      disabled: isAdmin || isProject,
    });

    const [activeTab, setActiveTab] = useState<EditorTab>('html');
    const [openHelp, setOpenHelp] = useState<OpenHelp>(null);
    const [evaluation, setEvaluation] = useState<WinEvaluation | null>(null);

    const editorRef = useRef<HtmlCssEditorPaneRef>(null);

    // URL objets ouvertes dans un onglet, liberees au demontage si elles ne
    // l'ont pas encore ete.
    const openedUrlsRef = useRef<Map<string, number>>(new Map());

    useEffect(() => {
      const opened = openedUrlsRef.current;
      return () => {
        opened.forEach((timer, url) => {
          window.clearTimeout(timer);
          URL.revokeObjectURL(url);
        });
        opened.clear();
      };
    }, []);

    // Repercute l'identifiant de reponse charge vers la couche de sauvegarde.
    useEffect(() => {
      setAnswerId(answerId);
    }, [answerId, setAnswerId]);

    // Signale une fois que le travail affiche vient d'une sauvegarde locale.
    // Le message est differe d'une micro-tache et annule au demontage, pour ne
    // pas apparaitre sur la page suivante si l'eleve quitte la lecon pendant le
    // chargement.
    useEffect(() => {
      if (!restoredFromLocal) return;

      const timer = window.setTimeout(() => {
        message.info(t('htmlCss.messages.restoredFromLocal'));
      }, 0);

      return () => window.clearTimeout(timer);
    }, [restoredFromLocal, t]);

    const handleHtmlChange = useCallback(
      (value: string) => {
        setHtml(value);
        onHtmlCodeChange?.(value);
      },
      [setHtml, onHtmlCodeChange]
    );

    const handleCssChange = useCallback(
      (value: string) => {
        setCss(value);
        onCssCodeChange?.(value);
      },
      [setCss, onCssCodeChange]
    );

    /**
     * Execute l'exercice : evalue le code et enregistre la reponse.
     *
     * Aucun message de reussite n'est emis ici. La reussite pedagogique est
     * annoncee par la vue parente (`LessonContent`), seule et traduite : c'est
     * l'empilement de ces deux sources qui produisait des notifications en
     * double, avec deux libelles differents.
     */
    const handleRun = useCallback(async () => {
      const result = evaluateWinConditions(workspace.html, workspace.css, config.winConditions);
      setEvaluation(result);

      if (!isAdmin) {
        const outcome = await save(workspace);
        if (outcome.status === 'local') {
          message.warning(t('htmlCss.messages.savedLocally'));
        } else if (outcome.status === 'failed') {
          message.error(t('htmlCss.messages.saveFailed', { reason: outcome.reason }));
        }
      }

      // Un exercice sans condition de reussite est un exercice libre : il ne
      // doit pas bloquer la progression. Auparavant aucun retour n'etait emis
      // et le bouton "Suivant" restait inaccessible.
      const succeeded = result.status === 'passed' || result.status === 'no-conditions';

      // Un projet ne fait pas partie d'un parcours : il n'y a pas de
      // progression a enregistrer, et l'identifiant transmis serait celui d'un
      // projet, que l'API des lecons rejette.
      if (succeeded && !isAdmin && !isProject && lesson?.id) {
        try {
          await saveProgressionUser(lesson.id);
        } catch {
          // La progression est rejouee au passage a la lecon suivante.
        }
      }

      checkResult(succeeded);
    }, [
      workspace,
      config.winConditions,
      isAdmin,
      isProject,
      save,
      saveProgressionUser,
      lesson?.id,
      checkResult,
      t,
    ]);

    /**
     * Enregistre la configuration de la lecon (mode administration seulement).
     *
     * Cote eleve, l'enregistrement est porte par "Executer" : un bouton dedie
     * n'aurait fait qu'un sous-ensemble de la meme action.
     */
    const handleSave = useCallback(() => {
      onAdminAction?.('save');
    }, [onAdminAction]);

    const handleRestore = useCallback(() => {
      if (isAdmin && onRestoreInitial) {
        onRestoreInitial();
        return;
      }
      restoreInitial();
      setEvaluation(null);
    }, [isAdmin, onRestoreInitial, restoreInitial]);

    /**
     * Ouvre le rendu dans un onglet distinct.
     *
     * `noopener` empeche la page ouverte d'acceder a la fenetre de la
     * plateforme, et le document est ecrit dans une URL objet plutot que via
     * `document.write` dans un contexte de meme origine.
     */
    const handleOpenInNewTab = useCallback(() => {
      const blob = new Blob([buildPreviewDocument(workspace)], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');

      // L'URL est liberee une fois l'onglet charge. Elle est aussi enregistree
      // pour etre liberee au demontage, si l'eleve quitte la lecon avant
      // l'echeance.
      const timer = window.setTimeout(() => {
        URL.revokeObjectURL(url);
        openedUrlsRef.current.delete(url);
      }, 60_000);
      openedUrlsRef.current.set(url, timer);
    }, [workspace]);

    const handleHelpSelect = useCallback(
      (code: string) => {
        // L'aide HTML alimente l'onglet HTML, les aides CSS et selecteurs
        // alimentent l'onglet CSS.
        editorRef.current?.insertSnippet(openHelp === 'html' ? 'html' : 'css', code);
        setOpenHelp(null);
      },
      [openHelp]
    );

    useImperativeHandle(ref, () => ({ getCurrentCode: () => workspace }), [workspace]);

    const helpDrawer = useMemo(() => {
      switch (openHelp) {
        case 'html':
          return { title: t('htmlCss.editor.htmlHelp'), items: config.htmlHelpItems };
        case 'css':
          return { title: t('htmlCss.editor.cssHelp'), items: config.cssHelpItems };
        case 'selector':
          return { title: t('htmlCss.editor.selectorHelp'), items: config.selectorHelpItems ?? [] };
        default:
          return null;
      }
    }, [openHelp, config, t]);

    if (loading) {
      return (
        <div className="htmlcss-loading">
          <Spin size="large" />
        </div>
      );
    }

    return (
      <div className="htmlcss-root">
        <HtmlCssStatement
          statement={statement ?? lesson?.statement}
          lessonId={lesson?.id}
        />

        <WinConditionFeedback evaluation={evaluation} />

        <ResponsiveHtmlCssLayout
          editorComponent={
            <HtmlCssEditorPane
              ref={editorRef}
              workspace={workspace}
              onHtmlChange={handleHtmlChange}
              onCssChange={handleCssChange}
              readOnly={!enableEditCode}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              htmlHelpItems={config.htmlHelpItems}
              cssHelpItems={config.cssHelpItems}
              selectorHelpItems={config.selectorHelpItems}
              onOpenHtmlHelp={() => setOpenHelp('html')}
              onOpenCssHelp={() => setOpenHelp('css')}
              onOpenSelectorHelp={() => setOpenHelp('selector')}
            />
          }
          previewComponent={<HtmlCssPreview workspace={workspace} />}
        />

        <div className="htmlcss-actions">
          <Button
            className="htmlcss-btn-secondary"
            icon={<RollbackOutlined />}
            onClick={handleRestore}
          >
            {t('htmlCss.actions.restore')}
          </Button>

          {/*
            Action principale et unique pour l'eleve : elle execute le code,
            l'enregistre et le corrige. Un bouton "Enregistrer" distinct
            existait mais ne faisait qu'un sous-ensemble de celle-ci, sans
            benefice pour l'eleve.
          */}
          <Button
            className="htmlcss-btn-run"
            icon={<PlayCircleOutlined />}
            onClick={handleRun}
            loading={saving}
          >
            {t('htmlCss.actions.run')}
          </Button>

          <Button
            className="htmlcss-btn-secondary"
            icon={<ExportOutlined />}
            onClick={handleOpenInNewTab}
          >
            {t('htmlCss.actions.openInNewTab')}
          </Button>

          {isAdmin && (
            <>
              <Button className="htmlcss-btn-secondary" onClick={() => onAdminAction?.('win')}>
                {t('htmlCss.actions.editWinConditions')}
              </Button>
              <Button className="htmlcss-btn-secondary" onClick={() => onAdminAction?.('help')}>
                {t('htmlCss.actions.editHelp')}
              </Button>
              <Button icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
                {t('htmlCss.actions.save')}
              </Button>
            </>
          )}
        </div>

        {helpDrawer && (
          <HelpDrawer
            open
            title={helpDrawer.title}
            items={helpDrawer.items}
            onClose={() => setOpenHelp(null)}
            onSelect={handleHelpSelect}
          />
        )}
      </div>
    );
  }
);

HtmlCssStudent.displayName = 'HtmlCssStudent';

export default HtmlCssStudent;
